import { z } from 'zod'

import {
  getNumberProperty,
  getStringProperty,
  getUnknownProperty,
} from './object-utils'

export interface ParsedApiErrorDetails {
  message: string
  status?: number
  statusText?: string
  url?: string
  body?: unknown
  rawResponseText?: string
}

const validationErrorsSchema = z.record(z.string(), z.array(z.string()))

const apiErrorBodySchema = z.union([
  z.string(),
  z
    .object({
      message: z.string().optional(),
      title: z.string().optional(),
      detail: z.string().optional(),
      status: z.number().optional(),
      type: z.string().optional(),
      errors: validationErrorsSchema.optional(),
    })
    .passthrough(),
])

// No `.strict()`: OpenAPI `ResponseError` uses a real Fetch `response` with many
// enumerable keys; strict parsing would drop the whole error shape and lose status.
const responseLikeSchema = z.object({
  status: z.number().optional(),
})

const apiErrorSchema = z.object({
  status: z.number().optional(),
  message: z.string().optional(),
  statusText: z.string().optional(),
  body: apiErrorBodySchema.optional(),
  response: responseLikeSchema.optional(),
})

export interface ParsedApiError {
  status?: number
  message?: string
  bodyMessage?: string
  bodyTitle?: string
  bodyDetail?: string
  errors?: Record<string, string[]>
}

function isResponseLike(
  value: unknown,
): value is Response & { clone: () => Response } {
  return (
    value instanceof Response ||
    (value != null &&
      typeof value === 'object' &&
      'status' in value &&
      'text' in value &&
      'clone' in value)
  )
}

function extractMessageFromBody(body: unknown): string | undefined {
  if (body == null) {
    return undefined
  }

  if (typeof body === 'string') {
    const trimmedBody = body.trim()
    return trimmedBody.length > 0 ? trimmedBody : undefined
  }

  if (
    typeof body === 'object' &&
    'message' in body &&
    typeof body.message === 'string' &&
    body.message.trim().length > 0
  ) {
    return body.message
  }

  return undefined
}

export function parseApiError(error: unknown): ParsedApiError | undefined {
  const result = apiErrorSchema.safeParse(error)
  if (!result.success) {
    if (error instanceof Error) {
      return error.message.length > 0 ? { message: error.message } : undefined
    }
    return undefined
  }

  const { status, message, body, response } = result.data
  const normalizedStatus = status ?? response?.status

  if (typeof body === 'string') {
    return {
      status: normalizedStatus,
      message,
      bodyMessage: body,
    }
  }

  const normalized = {
    status: normalizedStatus ?? body?.status,
    message,
    bodyMessage: body?.message,
    bodyTitle: body?.title,
    bodyDetail: body?.detail,
    errors: body?.errors,
  }

  if (
    normalized.status == null &&
    normalized.message == null &&
    normalized.bodyMessage == null &&
    normalized.bodyTitle == null &&
    normalized.bodyDetail == null &&
    normalized.errors == null
  ) {
    return undefined
  }

  return normalized
}

export function extractStatusCode(error: unknown): number | undefined {
  return parseApiError(error)?.status
}

export function hasHttpStatus(error: unknown, status: number): boolean {
  return extractStatusCode(error) === status
}

export function extractApiErrorMessage(error: unknown): string | undefined {
  const parsed = parseApiError(error)

  if (parsed == null) {
    return undefined
  }

  return (
    parsed.message ??
    parsed.bodyMessage ??
    parsed.bodyDetail ??
    parsed.bodyTitle
  )
}

export async function parseApiErrorDetails(
  error: unknown,
  fallback: string,
): Promise<ParsedApiErrorDetails> {
  const errorMessage = getStringProperty(error, 'message')
  const errorStatus = getNumberProperty(error, 'status')
  const errorStatusText = getStringProperty(error, 'statusText')
  const errorUrl = getStringProperty(error, 'url')
  const responseProperty = getUnknownProperty(error, 'response')

  const responseCandidate = isResponseLike(responseProperty)
    ? responseProperty
    : isResponseLike(error)
      ? error
      : null

  if (responseCandidate != null) {
    const response = responseCandidate.clone()

    let rawResponseText: string | undefined
    let body: unknown

    try {
      rawResponseText = await response.text()
      if (rawResponseText.trim().length > 0) {
        try {
          body = JSON.parse(rawResponseText)
        } catch {
          body = rawResponseText
        }
      }
    } catch {
      // response body unreadable, fall through
    }

    return {
      message:
        extractMessageFromBody(body) ??
        errorMessage ??
        (response.statusText || fallback),
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      body,
      rawResponseText,
    }
  }

  return {
    message: errorMessage ?? fallback,
    status: errorStatus,
    statusText: errorStatusText,
    url: errorUrl,
  }
}
